export const BUSINESS = {
  name: "Magnolia House Custom Picture Framing",
  short: "Magnolia House",
  phone: "(919) 552-6802",
  phoneHref: "tel:+19195526802",
  email: "magnoliahouse@live.com",
  address: "901 Jacobean Court, Fuquay-Varina, NC 27526",
  city: "Fuquay-Varina, NC",
  since: 1989,
  owner: "Shirley Hunsberger, CPF",
  mapsHref:
    "https://www.google.com/maps/search/?api=1&query=901+Jacobean+Court+Fuquay-Varina+NC+27526",
};

export const NAV = [
  { to: "/", label: "Home" },
  { to: "/services", label: "Services" },
  { to: "/gallery", label: "Framing Gallery" },
  { to: "/our-story", label: "Our Story" },
  { to: "/art", label: "Art Gallery" },
  { to: "/classes", label: "Art Classes" },
  { to: "/reviews", label: "Reviews" },
  { to: "/faq", label: "FAQ" },
  { to: "/directions", label: "Directions" },
  { to: "/contact", label: "Contact" },
] as const;

export const SERVICES = [
  {
    title: "Custom Framing",
    image: "mat-designs",
    body: "Custom and prebuilt frames in every style and size. Shirley designs each piece with you — frame profile, mat layers, and glazing chosen for your artwork, not pulled off a shelf.",
  },
  {
    title: "Shadow Boxes & Memorabilia",
    image: "shadow-box",
    body: "Jerseys, varsity letters, medals, coins, christening gowns, instruments, and other three-dimensional keepsakes mounted and framed so they hold their shape for decades.",
  },
  {
    title: "Diplomas & Documents",
    image: "framed-varsity",
    body: "Diplomas, certificates, commissions, and historical documents framed with archival mats and mounting that never damage the paper.",
  },
  {
    title: "Flags & Military Honors",
    image: "framed-flag",
    body: "Burial flags, service medals, patches, and photographs arranged into one dignified display for military and veteran families.",
  },
  {
    title: "Archival & Museum-Quality Framing",
    image: "framed-family-photos",
    body: "Acid-free rag mats, conservation mounting, and UV-filtering glazing for anything irreplaceable — original art, heirlooms, and one-of-a-kind photographs.",
  },
  {
    title: "Photograph Restoration",
    image: "photo-restoration",
    body: "Faded, torn, or water-damaged photographs restored and reprinted before framing, so the picture you remember is the one that goes on the wall.",
  },
  {
    title: "Canvas Stretching",
    image: "framed-mask",
    body: "Rolled canvases stretched properly over solid bars, plus float frames and gallery wraps finished to a professional standard.",
  },
  {
    title: "Needlepoint & Textile Lacing",
    image: "framed-book",
    body: "Needlework, cross-stitch, and textiles laced and blocked by hand — never glued or taped — so the stitching stays square and safe.",
  },
  {
    title: "Custom Mat Design",
    image: "frame-selection",
    body: "Multi-opening layouts, V-grooves, fabric wraps, and hand-cut lettering designed around your piece and the room it will live in.",
  },
  {
    title: "Hanging Service",
    image: "happy-customer",
    body: "Finished work delivered and hung — level, secure, and arranged the way it looked in the design consultation.",
  },
];

export const REASONS = [
  {
    stat: "35+",
    label: "Years framing in Fuquay-Varina",
    body: "Serving the same community since 1989.",
  },
  {
    stat: "CPF",
    label: "Certified Professional Framer",
    body: "Master's Certification through the PPFA.",
  },
  {
    stat: "1:1",
    label: "By appointment, with the owner",
    body: "Every design meeting is with Shirley herself.",
  },
  {
    stat: "100%",
    label: "Workmanship guaranteed",
    body: "Built to hang for generations, not seasons.",
  },
];

export const PROCESS = [
  {
    step: "01",
    title: "Call Shirley",
    body: "One phone call sets a time that works for you — most days and times, including after hours.",
  },
  {
    step: "02",
    title: "Bring your piece in",
    body: "We look at it together in the shop, in real light, with real frame and mat samples in hand.",
  },
  {
    step: "03",
    title: "Design the solution",
    body: "Frame profile, mat colors, glazing, and archival options chosen for your piece and your budget.",
  },
  {
    step: "04",
    title: "A custom quote",
    body: "You see the full price before anything is ordered. No pressure, no upsell, no surprises.",
  },
  {
    step: "05",
    title: "Built by hand",
    body: "Cut, fitted, and finished in the shop — then ready to collect, or hung on your wall for you.",
  },
];

export const FAQS = [
  {
    q: "How much does custom framing cost?",
    a: "Every project is quoted individually, because the price depends on the size of the piece, the frame profile, the number of mats, and the glazing you choose. Shirley will always show you options across a range of prices during your consultation, and you approve the quote before any work begins.",
  },
  {
    q: "Do I need an appointment?",
    a: "Yes — Magnolia House is by appointment so that you get undivided attention instead of waiting at a counter. Call (919) 552-6802 and Shirley will find a time, including evenings and weekends when needed.",
  },
  {
    q: "How long does custom framing take?",
    a: "Most projects are ready in about two to three weeks, depending on the materials ordered. If you are framing for a graduation, wedding, or gift deadline, say so when you call and Shirley will tell you honestly whether it can be done in time.",
  },
  {
    q: "What does 'archival' or 'conservation' framing mean?",
    a: "It means every material touching your artwork is acid-free and reversible: rag mats, conservation mounting, and UV-filtering glazing. Nothing is trimmed, glued, or permanently altered, so the piece can be removed years later in the same condition it went in.",
  },
  {
    q: "Can you frame something unusual — a jersey, a guitar, a wedding dress?",
    a: "Yes. Shadow-box and object framing is one of the specialties here. Jerseys, instruments, flags, medals, coins, tools, gowns, and masks have all been framed at Magnolia House.",
  },
  {
    q: "Can you fix an old or damaged photograph?",
    a: "Often, yes. Photograph restoration is offered in-house — faded, creased, torn, or water-damaged prints can usually be restored and reprinted before framing.",
  },
  {
    q: "Do you stretch canvases?",
    a: "Yes. Rolled or sagging canvases can be stretched over new bars and finished with a float frame or gallery wrap.",
  },
  {
    q: "Where are you located now?",
    a: "The shop moved in 2025 to 901 Jacobean Court in Fuquay-Varina, after 24 years on South Main Street. The new location is set up for relaxed, one-on-one design appointments.",
  },
  {
    q: "Do you sell original artwork or teach classes?",
    a: "Both. Shirley is a working artist in pastel and oils who takes portrait and landscape commissions, and she teaches art classes for adults and children ages 13 and up.",
  },
];

export const REVIEWS = [
  {
    quote:
      "Shirley took time to walk through every mat and frame option with us. Our daughter's diploma looks better than we imagined, and she explained exactly why she recommended what she did.",
    name: "Fuquay-Varina customer",
    source: "Google review",
  },
  {
    quote:
      "She framed my grandfather's flag and service medals together in one shadow box. It's the most meaningful thing hanging in our home.",
    name: "Wake County family",
    source: "Google review",
  },
  {
    quote:
      "Thirty-five years of experience shows. Beautiful work, fair price, and no pressure at all — just honest advice about what my painting needed.",
    name: "Local artist",
    source: "Google review",
  },
  {
    quote:
      "I brought in a torn photograph of my mother from the 1940s. She restored it and framed it archivally. I cried when I picked it up.",
    name: "Longtime client",
    source: "Google review",
  },
];

export const CLASSES = [
  {
    title: "Adult Classes",
    body: "Small-group instruction in pastel and oils for complete beginners through returning painters. Composition, value, colour mixing, and drawing from life — at your own pace.",
  },
  {
    title: "Young Artists, Ages 13+",
    body: "Serious young artists get real technique, real materials, and honest critique — the same fundamentals taught to adults, in a patient and encouraging studio.",
  },
  {
    title: "Format & Scheduling",
    body: "Classes run in small groups at the studio so everyone gets individual attention. Schedules and current openings change through the year — call Shirley for the current session.",
  },
  {
    title: "Commissions",
    body: "Shirley also accepts commissions for portraits and landscapes in pastel and oil, framed in-house if you'd like the whole piece finished under one roof.",
  },
];

export const ARTWORKS = [
  { image: "art-landscape-pastel", title: "Landscape", medium: "Pastel" },
  { image: "art-pastel-portrait", title: "Portrait Study", medium: "Pastel" },
  { image: "art-jordan-lake", title: "Jordan Lake, Plein Air", medium: "Oil" },
  { image: "art-sunflowers", title: "Sunflowers", medium: "Pastel" },
  { image: "art-river-trees", title: "River Trees", medium: "Oil" },
  { image: "art-lemons", title: "Lemons on Cloth", medium: "Oil" },
  { image: "art-balloons", title: "Balloon Festival", medium: "Oil" },
  { image: "art-woods-path", title: "Path Through the Woods", medium: "Oil" },
  { image: "art-pink-flowers", title: "Pink Blooms", medium: "Oil" },
  { image: "art-golden-field", title: "Golden Field", medium: "Oil" },
  { image: "art-mountain-color", title: "Mountain Colour", medium: "Pastel" },
  { image: "art-england", title: "English Landscape", medium: "Oil" },
  { image: "art-downtown-fv", title: "Downtown Fuquay-Varina", medium: "Oil" },
];

export const GALLERY = [
  { image: "framed-jersey-medal", caption: "Jersey, medal & photograph" },
  { image: "framed-guitar", caption: "Framed guitar" },
  { image: "framed-flag", caption: "American flag display" },
  { image: "framed-medals", caption: "Service medals" },
  { image: "shadow-box", caption: "Shadow box keepsake" },
  { image: "framed-coins", caption: "Coin collection" },
  { image: "framed-jersey", caption: "Sports jersey" },
  { image: "framed-varsity", caption: "Varsity letter" },
  { image: "framed-mask", caption: "Framed mask" },
  { image: "photo-restoration", caption: "Restored photograph" },
  { image: "framed-book", caption: "Framed book" },
  { image: "mat-designs", caption: "Custom mat design" },
  { image: "framed-family-photos", caption: "Family photographs" },
  { image: "happy-customer", caption: "A finished commission" },
];

export const STUDIO = [
  {
    image: "studio-frame-wall",
    caption: "Frame corner samples wall",
    meta: "Hundreds of profiles",
  },
  {
    image: "studio-design-counter",
    caption: "Design counter with finished pieces on the wall",
  },
  {
    image: "studio-worktable",
    caption: "Work table with a framed painting in progress",
  },
  {
    image: "studio-bench",
    caption: "Cutting and assembly bench with tools and frame stock",
  },
  {
    image: "studio-frame-corners",
    caption: "Ready-made frames and flat files behind the corner sample wall",
  },
  { image: "studio-matboards", caption: "Mat board storage rack" },
];
